import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ApolloQueryResult } from '@apollo/client';
import { EntitlementBaseProduct } from '@atlassian/cssxp-types-entitlement';
import { isFreePlanEntitlement, EntitlementEdition, isOfferingPaid } from '@atlassian/cssxp-utils-entitlement';
import { BillingFrequency } from '@atlassian/cssxp-ccp-types';
import Tooltip from '@atlaskit/tooltip';
import { Appearance } from '@atlaskit/button';
import {
  BillingStatus,
  EntitlementDetails,
  FetchInvoiceGroupDetailsPpcQuery,
} from '@css-xp/graphtypes/src/generated-types-new';
import {
  MoreActionsItems,
  MoreActionsV2,
  OpenSidebarPayload,
  useModalDialog,
  useSidebar,
  useApplicationContext,
  useFeatureToggle,
} from '@css-xp/base';
import { InfoDialog } from '@css-xp/info-dialog';
import { isAccessProduct } from '@atlassian/cssxp-utils-product';
import { analytics, createOpenSidebarFromButtonEvent, AnalyticsTrackSubject } from '@atlassian/temp-analytics';
import {
  getPartnerServiceDeskUrlForNewQuoteHelp,
  getPartnerServiceDeskUrlForGenericHelp,
} from '@css-xp/ppc-navigation';
import {
  getChangeBillDateSidebarPayload,
  isActiveEntitlement,
  isTrialEntitlement,
  validateFreeSubscriptions,
} from '@css-xp/entitlements-v2';
import {
  isCollectionProductEntitlement,
  isCollectionInstanceEntitlement,
  getRouteUrl,
  isParentAppInstance,
  isMultiInstanceAppInstance,
} from '@css-xp/ppc-utils';
import {
  ENABLE_PREMIUM_ANNUAL_TRIAL_IN_PPC,
  STATSIG_FEATURE_GATES,
  routePaths,
  SUBSCRIPTION_CONSTANTS,
} from '@css-xp/ppc-constants';
import { SIDEBAR } from '../../constants/sidebar';
import { FormTrackerStepNames } from '../../sidebar-panel/change-subscription-pane/change-subscription-pane';

interface EntitlementHeaderActionsProps {
  entitlement: EntitlementDetails;
  invoiceGroupDetails: ApolloQueryResult<FetchInvoiceGroupDetailsPpcQuery | undefined>;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  menuButtonAppearance?: Appearance;
  shouldShowAddOnDetails?: boolean;
  isRovoOrgProduct?: boolean;
}

export enum ENTITLEMENT_ACTIONS_NAME {
  CHANGE_SUBSCRIPTION = 'Change subscription',
  OPT_OUT = 'End trial',
  CHANGE_BILL_DATE = 'Change bill date',
  SWITCH_BILLING_PROFILE = 'Switch billing profile',
  REQUEST_CHANGE = 'Request change',
  START_PREMIUM_TRIAL = 'Start premium trial',
  CONTACT_SUPPORT = 'Contact support',
  START_ADVANCED_TRIAL = 'Start advanced trial',
}

const defaultSidebarOptions = {
  isOverlay: true,
  sidebarWidth: '600px',
};

export const EntitlementHeaderActions = ({
  entitlement,
  invoiceGroupDetails,
  refetch,
  menuButtonAppearance = 'primary',
  shouldShowAddOnDetails = false,
  isRovoOrgProduct,
}: EntitlementHeaderActionsProps) => {
  const [isCollectionInstanceInfoModalOpen, setIsCollectionInstanceInfoModalOpen] = useState(false);

  const txaId = entitlement?.transactionAccountId ?? '';
  const { openSidebar } = useSidebar();
  const [isEnabled] = useFeatureToggle();
  const history = useHistory();
  let isSwitchBillingProfileEnabled = false;
  const isNewBillingProfileBehaviourEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_NEW_CHANGE_BILLING_PROFILE_BEHAVIOUR
  );
  const isAddOnChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ADD_ONS);
  const isSandboxDecouplingEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_SANDBOX_DECOUPLING);
  const isDesignIteration1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DESIGN_ITERATION_1);
  const isRovoAmendmentsDisabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_RESTRICT_ROVO_AMENDMENTS) && isRovoOrgProduct;

  const partnerData = useApplicationContext();
  const selectedPartnerAccountData = partnerData.partnerAccounts?.find(
    ({ transactionAccountId }: { transactionAccountId: string }) => transactionAccountId === txaId
  );

  const handleSidebarAction = (sidebarType: string, openSidebarPayload: OpenSidebarPayload) => {
    analytics.sendUIEvent(
      createOpenSidebarFromButtonEvent(sidebarType, '', [], {
        entitlementId: entitlement?.id,
      })
    );
    openSidebar(openSidebarPayload);
  };

  const isItAccessProduct = isAccessProduct(entitlement?.productId);
  const isPremiumAnnualTrialEnabled = isEnabled(ENABLE_PREMIUM_ANNUAL_TRIAL_IN_PPC);
  const isSandboxSupportForSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS
  );

  const isFutureDatedEntitlementEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_SUPPORT_FOR_FUTURE_DATED_ENTITLEMENTS);
  const isTWCPhase1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_1);
  const isTWCPhase2Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_2);
  const isMultiInstanceAppsEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_INSTANCE_APPS);

  const changeBillDatePayload = getChangeBillDateSidebarPayload(
    entitlement,
    refetch,
    isAddOnChangesEnabled,
    isSandboxSupportForSubscriptionsEnabled,
    isSandboxDecouplingEnabled
  );
  const isActive = isActiveEntitlement(entitlement);
  const isFreeEntitlement = validateFreeSubscriptions(entitlement);
  const isCollectionProduct = isTWCPhase1Enabled && isCollectionProductEntitlement(entitlement?.productId);
  const isCollectionInstance =
    isTWCPhase1Enabled && isCollectionInstanceEntitlement(entitlement?.relatesFromEntitlements as any);

  const isEntitlementOnStandardPaidPlan =
    isOfferingPaid(entitlement) && entitlement.offering?.name?.toUpperCase() === EntitlementEdition.STANDARD;

  const isEnterpriseProduct = entitlement?.edition === 'Enterprise';
  const isEnterpriseInstanceProduct = entitlement?.edition === 'Enterprise Instance';
  const isParentAppInstanceProduct = isMultiInstanceAppsEnabled && isParentAppInstance(entitlement?.offering?.id);
  const isMultiInstanceAppInstanceProduct =
    isMultiInstanceAppsEnabled && isMultiInstanceAppInstance(entitlement?.offeringId);

  const isAddOnProduct = isAddOnChangesEnabled && entitlement?.isAddOn;

  const isSandboxEntitlement = isSandboxSupportForSubscriptionsEnabled && entitlement?.isSandbox;
  const isSandboxProduct = isSandboxEntitlement && !entitlement?.isApp;
  const isExternallyBilledProduct = entitlement?.isExternallyBilled;
  const isAppEditionSupportEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_APP_EDITION_SUPPORT);

  const isFutureDatedInActiveEntitlement =
    isFutureDatedEntitlementEnabled &&
    entitlement?.isFutureDatedSubscription &&
    entitlement?.billingStatus === BillingStatus.INACTIVE; // net new future dated entitlement case

  const isFutureDatedActiveEntitlement =
    isFutureDatedEntitlementEnabled &&
    entitlement?.isFutureDatedSubscription &&
    entitlement?.billingStatus === BillingStatus.ACTIVE; // Free to annual future dated entitlement case

  isSwitchBillingProfileEnabled = (() => {
    if ((isCollectionProduct && !isTWCPhase2Enabled) || (isCollectionInstance && !isTWCPhase2Enabled)) {
      return false;
    }

    if (isFreeEntitlement) {
      return true;
    }
    //if entitlement is access product then switch billing profile should be behind FF
    if (isItAccessProduct) {
      return true;
    }

    if (isEnterpriseProduct || isEnterpriseInstanceProduct) {
      return false;
    }

    if (isExternallyBilledProduct) {
      return true;
    }

    if (isFutureDatedActiveEntitlement) {
      return true;
    }

    if (isFutureDatedInActiveEntitlement) {
      return false;
    }

    if (isParentAppInstanceProduct || isMultiInstanceAppInstanceProduct) {
      return false;
    }

    return true;
  })();

  const { openModalDialog } = useModalDialog();

  const isEntitlementEligibleForChangeSubscription = useCallback(() => {
    const isUnlimitedDiscountPresent = entitlement?.promotions?.some((promotion) => {
      const benefitsObject = promotion?.benefits?.[0];
      if (
        benefitsObject?.benefitType === 'DISCOUNT' &&
        benefitsObject?.duration === 'FOREVER' &&
        benefitsObject?.subBenefitType === 'PERCENTAGE' &&
        benefitsObject?.value === 100
      ) {
        return true;
      }
    });
    const isTrialSubscription = isTrialEntitlement(
      entitlement,
      entitlement.baseProduct === EntitlementBaseProduct.STATUS_PAGE
    );
    if (isUnlimitedDiscountPresent || isTrialSubscription || isFreePlanEntitlement(entitlement)) {
      return true;
    }
    return invoiceGroupDetails?.data?.invoiceGroup?.defaultPaymentMethod && selectedPartnerAccountData?.billTo;
  }, [entitlement, invoiceGroupDetails?.data?.invoiceGroup?.defaultPaymentMethod, selectedPartnerAccountData?.billTo]);

  const isAnnualBillingFrequency = entitlement?.billingFrequency === BillingFrequency.ANNUAL;

  const isChangeBillDateEnabled = (() => {
    if ((isCollectionProduct && !isTWCPhase2Enabled) || isCollectionInstance) {
      return false;
    }

    if (isParentAppInstanceProduct || isMultiInstanceAppInstanceProduct) {
      return false;
    }

    // if entitlement is access product and not annual billing frequency then change bill date should be behind FF
    if (isItAccessProduct && !isAnnualBillingFrequency) {
      return true;
    }

    if (isExternallyBilledProduct) {
      return false;
    }

    if (isEnterpriseProduct || isEnterpriseInstanceProduct) {
      return false;
    }

    if (isSandboxProduct) {
      return false;
    }

    // if entitlement is not free and not annual billing frequency then change bill date should be enabled
    if (!isFreeEntitlement && !isAnnualBillingFrequency) {
      return true;
    }

    if (isFutureDatedActiveEntitlement || isFutureDatedInActiveEntitlement) {
      return false;
    }

    return false;
  })();

  const isOptoutEnabled = (() => {
    if (
      isActive &&
      isMultiInstanceAppsEnabled &&
      isParentAppInstanceProduct &&
      (!!entitlement?.upgradeTrial || !!entitlement?.isOptOutAllowed)
    ) {
      return true;
    }

    return (
      isActive &&
      (!!entitlement?.upgradeTrial || !!entitlement?.isOptOutAllowed) &&
      !isAnnualBillingFrequency &&
      !isExternallyBilledProduct &&
      !isFutureDatedActiveEntitlement &&
      !isFutureDatedInActiveEntitlement &&
      !(isCollectionProduct && !isTWCPhase2Enabled) &&
      !isCollectionInstance
    );
  })();

  // enables start premium trial option for annual billing and standard paid plan
  const isEntitlementEligibleToStartPremiumTrial =
    isPremiumAnnualTrialEnabled &&
    isAnnualBillingFrequency &&
    isEntitlementOnStandardPaidPlan &&
    !entitlement?.isApp &&
    !isAddOnProduct &&
    !isExternallyBilledProduct &&
    isActive &&
    !isSandboxEntitlement &&
    !isFutureDatedActiveEntitlement &&
    !isFutureDatedInActiveEntitlement &&
    !isCollectionProduct &&
    !isCollectionInstance &&
    !isParentAppInstanceProduct &&
    !isMultiInstanceAppInstanceProduct;

  const isEntitlementEligibleToStartAdvancedTrial =
    isPremiumAnnualTrialEnabled &&
    entitlement?.isApp &&
    isAnnualBillingFrequency &&
    isActive &&
    isEntitlementOnStandardPaidPlan &&
    !isAddOnProduct &&
    !isExternallyBilledProduct &&
    !isSandboxEntitlement &&
    !isFutureDatedActiveEntitlement &&
    !isFutureDatedInActiveEntitlement &&
    !isCollectionProduct &&
    !isCollectionInstance &&
    !isParentAppInstanceProduct &&
    !isMultiInstanceAppInstanceProduct;

  let isRequestChangeEnabled = false;
  if (isExternallyBilledProduct) {
    isRequestChangeEnabled = isActive && !isFutureDatedActiveEntitlement && !isFutureDatedInActiveEntitlement;
  } else if (isEnterpriseInstanceProduct || isEnterpriseProduct) {
    isRequestChangeEnabled = isActive && !isFutureDatedActiveEntitlement && !isFutureDatedInActiveEntitlement;
  } else if (isMultiInstanceAppInstanceProduct) {
    isRequestChangeEnabled = isActive;
  } else {
    isRequestChangeEnabled =
      isActive &&
      isAnnualBillingFrequency &&
      isNewBillingProfileBehaviourEnabled &&
      !isFutureDatedActiveEntitlement &&
      !isFutureDatedInActiveEntitlement &&
      !(isCollectionProduct && !isTWCPhase2Enabled) &&
      !isCollectionInstance;
  }

  const detailPageActions: MoreActionsItems[] = [
    {
      id: SIDEBAR.CHANGE_SUBSCRIPTION,
      content: ENTITLEMENT_ACTIONS_NAME.CHANGE_SUBSCRIPTION,
      action: () => {
        if (isCollectionInstance) {
          setIsCollectionInstanceInfoModalOpen(true);
        } else if (isEntitlementEligibleForChangeSubscription()) {
          handleSidebarAction(SIDEBAR.CHANGE_SUBSCRIPTION, {
            sidebarType: SIDEBAR.CHANGE_SUBSCRIPTION,
            payload: {
              entitlement,
              txaId,
              refetch,
              showSkipTrialCheckbox: false,
              isAddOnChangesEnabled: shouldShowAddOnDetails,
            },
            sidebarOptions: isDesignIteration1Enabled ? undefined : defaultSidebarOptions,
          });
        } else {
          openModalDialog();
        }
      },
      // Enable it for Upgrade trial flows as part of https://hello.atlassian.net/browse/ITFO-9724
      predicate:
        isActive &&
        !entitlement?.upgradeTrial &&
        !isAnnualBillingFrequency &&
        !isItAccessProduct &&
        !isAddOnProduct &&
        !(isEnterpriseProduct || isEnterpriseInstanceProduct) &&
        !isRovoOrgProduct &&
        !isSandboxEntitlement &&
        !isExternallyBilledProduct &&
        !isFutureDatedActiveEntitlement &&
        !isFutureDatedInActiveEntitlement &&
        !(isCollectionProduct && !isTWCPhase2Enabled) &&
        !(isCollectionInstance && !isTWCPhase2Enabled) &&
        !isParentAppInstanceProduct &&
        !isMultiInstanceAppInstanceProduct,
    },
    {
      id: 'opt-out',
      content: ENTITLEMENT_ACTIONS_NAME.OPT_OUT,
      action: () =>
        handleSidebarAction(SIDEBAR.OPTOUT_TRIAL, {
          sidebarType: SIDEBAR.OPTOUT_TRIAL,
          payload: {
            entitlementId: entitlement.id,
            entitlement,
            isDesignIteration1Enabled,
            refetch,
            isTWCPhase2Enabled,
            isAppEditionSupportEnabled,
          },
          sidebarOptions: isDesignIteration1Enabled ? undefined : defaultSidebarOptions,
        }),
      predicate: isOptoutEnabled,
    },
    {
      id: 'change-bill-date',
      content: ENTITLEMENT_ACTIONS_NAME.CHANGE_BILL_DATE,
      action: () =>
        handleSidebarAction(changeBillDatePayload.sidebar, {
          ...changeBillDatePayload,
          sidebarType: changeBillDatePayload.sidebar,
          sidebarOptions: isDesignIteration1Enabled ? undefined : defaultSidebarOptions,
        }),
      predicate: isActive && isChangeBillDateEnabled && !isRovoAmendmentsDisabled,
      isDisabled: !!entitlement?.upgradeTrial,
    },
    {
      id: 'switch-billing-profile',
      content: entitlement?.hasPendingInvoices ? (
        <Tooltip
          content="You’ll be able to switch the billing profile once the current invoice has finished generating."
          tag="span"
        >
          <span>{ENTITLEMENT_ACTIONS_NAME.SWITCH_BILLING_PROFILE}</span>
        </Tooltip>
      ) : (
        ENTITLEMENT_ACTIONS_NAME.SWITCH_BILLING_PROFILE
      ),
      isDisabled: !!entitlement?.hasPendingInvoices,
      action: () => {
        if (isCollectionInstance) {
          setIsCollectionInstanceInfoModalOpen(true);
        } else {
          handleSidebarAction(AnalyticsTrackSubject.SWITCH_BILLING_PROFILE, {
            sidebarType: SIDEBAR.SET_INVOICE_GROUP,
            payload: {
              entitlement,
              refetch,
              isSandboxDecouplingEnabled,
            },
            sidebarOptions: isDesignIteration1Enabled ? undefined : defaultSidebarOptions,
          });
        }
      },
      predicate: isActive && isSwitchBillingProfileEnabled,
    },
    {
      id: 'request-change',
      content: ENTITLEMENT_ACTIONS_NAME.REQUEST_CHANGE,
      action: () => {
        window.open(getPartnerServiceDeskUrlForNewQuoteHelp(), '_blank');
      },
      predicate: isRequestChangeEnabled,
    },
    {
      id: 'start-premium-trial',
      content: ENTITLEMENT_ACTIONS_NAME.START_PREMIUM_TRIAL,
      action: () =>
        handleSidebarAction(SIDEBAR.CHANGE_SUBSCRIPTION, {
          sidebarType: SIDEBAR.CHANGE_SUBSCRIPTION,
          payload: {
            entitlement,
            txaId,
            refetch,
            initialFormTrackerStep: FormTrackerStepNames.REVIEW,
            hideProgressTracker: true,
            isEligibleForPremiumTrial: isEntitlementEligibleToStartPremiumTrial,
            formState: {
              skipTrial: false,
              newPlan: null,
              userTier: null,
              billingFrequency: BillingFrequency.ANNUAL,
            },
          },
          sidebarOptions: isDesignIteration1Enabled ? undefined : defaultSidebarOptions,
        }),
      predicate: isEntitlementEligibleToStartPremiumTrial,
    },
    {
      id: 'start-advanced-trial',
      content: ENTITLEMENT_ACTIONS_NAME.START_ADVANCED_TRIAL,
      action: () =>
        handleSidebarAction(SIDEBAR.CHANGE_SUBSCRIPTION, {
          sidebarType: SIDEBAR.CHANGE_SUBSCRIPTION,
          payload: {
            entitlement,
            txaId,
            refetch,
            initialFormTrackerStep: FormTrackerStepNames.REVIEW,
            hideProgressTracker: true,
            isEligibleForAdvancedTrial: isEntitlementEligibleToStartAdvancedTrial,
            formState: {
              skipTrial: false,
              newPlan: null,
              userTier: null,
              billingFrequency: BillingFrequency.ANNUAL,
            },
          },
          sidebarOptions: isDesignIteration1Enabled ? undefined : defaultSidebarOptions,
        }),
      predicate: !!isEntitlementEligibleToStartAdvancedTrial,
    },
    {
      id: 'contact-support',
      content: ENTITLEMENT_ACTIONS_NAME.CONTACT_SUPPORT,
      action: () => {
        window.open(getPartnerServiceDeskUrlForGenericHelp(), '_blank');
      },
      predicate: (isCollectionProduct && !isTWCPhase2Enabled) || (isCollectionInstance && !isTWCPhase2Enabled),
    },
  ];

  return (
    <div className="button-toolbar" data-testid="header-actions">
      <InfoDialog
        isOpen={isCollectionInstanceInfoModalOpen}
        onClose={() => setIsCollectionInstanceInfoModalOpen(false)}
        infoContent="Products included in a collection can’t be updated individually. To make this change, you’ll need to update the collection that this plan is included in."
        title="Update the Collection"
        submitButtonText="Go to collection"
        handleSubmit={() => {
          const collectionParentId = entitlement?.relatesFromEntitlements?.find(
            (relatedEntitlement) =>
              (relatedEntitlement?.relationshipType as unknown) === SUBSCRIPTION_CONSTANTS.COLLECTION_ENTITLEMENT_TYPE
          )?.entitlementId;
          if (collectionParentId) {
            history.push(
              getRouteUrl(routePaths.entitlement, {
                txaId: txaId,
                entitlementId: collectionParentId,
              })
            );
          }
        }}
      />
      <MoreActionsV2
        actions={detailPageActions}
        trigger="Manage"
        ariaLabel="menu"
        isLoading={invoiceGroupDetails.loading}
        appearance={menuButtonAppearance}
      />
    </div>
  );
};
